import React from 'react';
import './WhyVintage.css';

const WhyVintage: React.FC = () => {
  return (
    <div className="why-vintage">
      <div className="vintage-header">
        <h1>༺ Why Vintage? ༻</h1>
      </div>

      <div className="container">
        <div className="vintage-content">
          
          <section className="intro-section">
            <p className="intro-text">
              Vintage isn't just a fashion choice, it's a mindset, a statement, and a quiet rebellion against everything fast, disposable, and overproduced. It's about slowing down, consuming consciously, and choosing pieces with soul, history, and intention.
            </p>
            
            <p className="highlight-text">
              We believe vintage is the future of fashion, not only because it's sustainable, but because it carries meaning.
            </p>
            
            <p>
              Each piece holds the energy of a past era and becomes something entirely new when you wear it. That transformation is personal and expressive. It's not about following trends, it's about making your own.
            </p>
            
            <p>
              At Eco Fashion, we don't just resell clothes. We curate, style, or rework each piece. Everything is handpicked, thoughtfully presented, and often redesigned in our in-house atelier. We create collections that feel cohesive, wearable, and inspiring, a vintage experience that feels fresh and intuitive without the overwhelm.
            </p>
            
            <p className="closing-text">
              We want you to connect with what you wear. To play, experiment, and express yourself honestly. Because fashion should reflect you, not just what's trending.
            </p>
          </section>

          <div className="section-divider">⸻</div>

          <section className="why-choose-section">
            <h2>★ Why choose vintage?</h2>
            
            <p className="section-intro"><strong>Every vintage piece</strong></p>
            <ul className="vintage-benefits">
              <li>Carries the energy and aesthetics of the era it comes from</li>
              <li>Is one of a kind, made before algorithms shaped trends</li>
              <li>Embodies sustainability by extending the life of what already exists</li>
              <li>Was often crafted with quality and care, built to last</li>
            </ul>
            
            <p className="statement-text">
              <strong>Wearing vintage isn't about costume</strong><br />
              It's about reclaiming style on your own terms
            </p>
          </section>

          <div className="section-divider">⸻</div>

          <section className="curated-section">
            <h2>★ Curated Vintage vs Secondhand</h2>
            
            <p><strong>Not all secondhand is the same.. and vintage isn't just used</strong></p>
            
            <p>
              Traditional secondhand shops often stock piles of clothing with little filtering or vision. It can feel chaotic and uninspiring, like digging through randomness.
            </p>
            
            <p className="highlight-text">Curated vintage is different. It's thoughtful.</p>
            
            <p>
              At Eco Fashion, every piece is hand-selected for its uniqueness, quality, and visual impact. We build collections around a vibe, a mood, a story. You're not just shopping for clothes, you're stepping into a curated world. Many pieces are reworked or styled in new ways to give them fresh relevance.
            </p>
            
            <p className="statement-text">
              <strong>We remove the overwhelm so you can focus on what matters! Finding pieces that truly resonate with you!</strong>
            </p>
          </section>

          <div className="section-divider">⸻</div>

          <section className="environmental-section">
            <h2>★ The environmental shift</h2>
            
            <p>Fast fashion thrives on overproduction, waste, and constant newness. It pollutes, exploits, and encourages a throwaway mindset.</p>
            
            <p className="highlight-text">Vintage slows everything down.</p>
            
            <p><strong>By choosing vintage, you</strong></p>
            <ul className="environmental-benefits">
              <li>Cut down on waste and unnecessary production</li>
              <li>Support a circular fashion system</li>
              <li>Lower your environmental footprint</li>
              <li>Invest in clothes that are made to last</li>
            </ul>
            
            <p className="statement-text">
              <strong>Every vintage purchase is a quiet protest</strong><br />
              A conscious choice for the planet, your style, and the future
            </p>
          </section>

          <div className="section-divider">⸻</div>

          <section className="rework-section">
            <h2>★ From Old to Bold: Our Rework Process</h2>
            
            <p className="section-intro"><strong>We don't just collect vintage, we evolve it</strong></p>
            
            <p>
              Our in-house atelier is where forgotten pieces are reimagined into something bold, wearable, and modern. From subtle tailoring to full transformations, we make vintage feel current without losing its character.
            </p>
            
            <p className="statement-text">
              <strong>Our studio is open and visible inside the shop because we believe in sharing the process, not hiding it. Real work, real hands, real creativity</strong>
            </p>
          </section>

          <div className="section-divider">⸻</div>

          <section className="anti-rules-section">
            <h2>★ Our Anti-Rules of Style ★</h2>
            
            <ul className="anti-rules">
              <li>Dress for yourself, not the feed</li>
              <li>Mix eras, clash prints, layer wildly</li>
              <li>Gender is not a dress code</li>
              <li>Repeat outfits proudly</li>
              <li>Repair it, restyle it, rework it</li>
              <li>Keep fashion weird</li>
            </ul>
          </section>

          <div className="section-divider">⸻</div>

          <section className="founder-note">
            <h2>‪‪❤︎‬ A Note from the Founder ‪‪❤︎‬</h2>
            
            <p className="founder-quote">
              "I started Eco Fashion because I wanted to love what I wear and feel good about how I got it. Vintage, to me, is freedom. It's creative, imperfect, and real. It's how I express myself without saying a word."
            </p>
          </section>

          <div className="section-divider">⸻</div>

          <section className="closing-section">
            <h2>★ Not convinced yet?</h2>
            
            <p>Stop by our space and let the clothes do the talking.</p>
            <p>Sometimes all it takes is trying something on to feel the difference.</p>
            
            <p className="highlight-text">
              We're always here to help you explore, discover, and fall in love with the vintage way.
            </p>
            
            <p className="contact-info">
              Find us in the heart of Thessaloniki — or reach out on Instagram <a href="https://instagram.com/eco.fashion.living" target="_blank" rel="noopener noreferrer">@eco.fashion.living</a> or at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default WhyVintage;